import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Header from './Header';
import Footer from './Footer';
import MonitoringDashboard from './MonitoringDashboard';
import './Dashboard.css';
import { fetchFirstFnolJson } from '../utils/fnolApi';
import { openDocumentUploadBucket } from '../utils/documentUploadLink';

const formatUpdatedAt = (value) => {
  if (!value) return 'Waiting for claims';
  return `Updated ${new Date(value).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const daysBetween = (startValue, endValue = new Date()) => {
  if (!startValue) return 0;
  const start = new Date(startValue).getTime();
  if (!Number.isFinite(start)) return 0;
  return Math.max(0, Math.floor((endValue.getTime() - start) / 86400000));
};

const countBy = (items, getKey) =>
  items.reduce((acc, item) => {
    const key = getKey(item) || 'Unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

const getClaimIdFromFnol = (record = {}) => {
  if (record.claim_id) return record.claim_id;
  if (typeof record.fnol_id === 'string' && record.fnol_id.startsWith('F-')) {
    return record.fnol_id.replace(/^F-/, 'C-');
  }
  return '';
};

const hiddenClaimIds = new Set(['C-260821-006', 'C-260831-001']);
const isHiddenClaimId = (claimId = '') => hiddenClaimIds.has(claimId);

const getFnolIdFromClaim = (claimId = '') =>
  typeof claimId === 'string' && claimId.startsWith('C-')
    ? claimId.replace(/^C-/, 'F-')
    : '';

const formatSummaryValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(formatSummaryValue).filter(Boolean).join(' ');
  }

  if (value && typeof value === 'object') {
    return Object.entries(value)
      .map(([key, child]) => {
        const text = formatSummaryValue(child);
        return text ? `${key.replaceAll('_', ' ')}: ${text}` : '';
      })
      .filter(Boolean)
      .join(' ');
  }

  return String(value || '')
    .replace(/\*\*/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const getFirstFieldValue = (record, keys) => {
  for (const key of keys) {
    const value = record?.[key];
    const formattedValue = formatSummaryValue(value);
    if (formattedValue) return formattedValue;
  }
  return '';
};

const formatCurrencyAmount = (value) => {
  const text = formatSummaryValue(value);
  if (!text) return '';

  const labeledMatch = text.match(
    /Estimated\s+Financial\s+Exposure\s*:\s*(\$?\s*[\d,]+(?:\.\d{1,2})?)/i
  );
  const rawAmount =
    labeledMatch?.[1] || text.match(/\$\s*[\d,]+(?:\.\d{1,2})?/)?.[0] || '';

  if (!rawAmount) return '';

  const numericValue = Number(rawAmount.replace(/\s+/g, '').replace(/[$,]/g, ''));
  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      maximumFractionDigits: 0,
      })
    : '';
};

const getExposureAmount = ({ claim = {}, formData = {}, insights = {} } = {}) => {
  const combinedRecord = { ...claim, ...formData, ...insights };
  const directCurrency = formatCurrencyAmount(
    getFirstFieldValue(combinedRecord, [
      'estimated_financial_exposure',
      'Estimated_Financial_Exposure',
      'exposure_amount',
      'probable_amount_entire_loss',
    ])
  );

  if (directCurrency) return directCurrency;

  return formatCurrencyAmount(
    combinedRecord.Exposure_Determination_Agent_Insights ||
      combinedRecord.Exposure_Determination_Agent_Next_Best_Action
  );
};

const getAlertPriority = (category = '') => {
  const normalized = String(category || '').toLowerCase();
  if (['litigation', 'siu', 'sui', 'liability'].includes(normalized)) return 'High';
  if (['damage', 'subrogation'].includes(normalized)) return 'Medium';
  if (['inquiry', 'coverage'].includes(normalized)) return 'Low';
  return '';
};

const priorityRank = {
  High: 3,
  Medium: 2,
  Low: 1,
};

const qaDonutColors = ['#22e39a', '#20c9ff', '#ffd28a', '#ff8a50', '#ff4b55'];

const insightCategoryMappings = [
  ['Inquiry', ['Notice_Of_Loss_Agent_Insights', 'FNOL_Reviewer_Agent_Insights']],
  ['Coverage', ['Coverage_Agent_Insights', 'Policy_Checker_Agent_Insights']],
  ['Liability', ['Liability_Agent_Insights']],
  ['Litigation', ['Litigation_Agent_Insights', 'Legal_Record_Reviewer_Agent_Insights']],
  ['Damage', ['Damage_Agent_Insights', 'Medical_Records_Reviewer_Agent_Insights', 'Bills_Invoices_Analyzer_Agent_Insights']],
  ['Subrogation', ['Subrogation_Agent_Insights', 'Witness_Reviewer_Agent_Insights', 'Lease_Contract_Document_Reviewer_Agent_Insights']],
  ['SIU', ['SUI_Agent_Insights', 'Fraud_Detection_Agent_Insights', 'Fraud_Compliance_Detector_Agent_Insights']],
];

const getClaimCategoriesFromInsights = (insights = {}) =>
  insightCategoryMappings
    .filter(([, keys]) => keys.some((key) => getFirstFieldValue(insights, [key])))
    .map(([category]) => category);

const getPrimaryCategory = (categories = []) =>
  categories.reduce((best, category) => {
    const rank = priorityRank[getAlertPriority(category)] || 0;
    const bestRank = priorityRank[getAlertPriority(best)] || 0;
    return rank > bestRank ? category : best;
  }, categories[0] || '');

const normalizeLineOfBusiness = (value = '') => {
  const normalized = String(value || '').toLowerCase();

  if (/\b(vehicle|motor|car|collision)\b/.test(normalized)) return 'Auto';
  if (/\b(property|home|roof|storm|wind|flood)\b/.test(normalized)) return 'Property';
  if (/\b(general\s*liability|liability|cgl|bodily\s*injury|slip|fall)\b/.test(normalized)) {
    return 'General Liability';
  }

  return '';
};

const getClaimLineOfBusiness = (claim = {}) => {
  const directLine = getFirstFieldValue(claim, [
    'line_of_business',
    'lineOfBusiness',
    'lob',
    'business_line',
    'policy_type',
    'policy_category',
    'coverage_type',
    'type_of_loss',
    'loss_type',
  ]);
  const directMatch = normalizeLineOfBusiness(directLine);

  if (directMatch) return directMatch;

  const contextMatch = normalizeLineOfBusiness(
    [
      claim.policy_number,
      claim.loss_summary,
      claim.damage_description,
      claim.claim_summary,
      claim.customer_name,
    ]
      .filter(Boolean)
      .join(' ')
  );

  return contextMatch || 'Uncategorized';
};

const getPriorityScore = (claim, priority) => {
  const baseScore = {
    High: 90,
    Medium: 60,
    Low: 30,
  }[priority] || 0;

  return Math.min(99, baseScore + Math.min(daysBetween(claim.created_at), 9));
};

const getQualityPercent = (score = {}) => {
  if (score.quality_percent !== undefined && score.quality_percent !== null) {
    const percent = Number(score.quality_percent);
    if (Number.isFinite(percent)) return percent;
  }

  if (typeof score.quality_score === 'string' && score.quality_score.includes('/')) {
    const [checked, total] = score.quality_score.split('/').map(Number);
    if (total) return Math.round((checked / total) * 100);
  }

  const rawScore = score.quality_score || score.qualityScore;
  const numericScore = Number(String(rawScore || '').replace('%', ''));
  return Number.isFinite(numericScore) ? numericScore : null;
};

const formatQualityScore = (score = {}) => {
  const percent = getQualityPercent(score);
  return percent === null ? '--' : `${Math.round(percent)}%`;
};

const getQualityTone = (percent) => {
  if (percent === null) return 'empty';
  if (percent >= 85) return 'high';
  if (percent >= 65) return 'medium';
  return 'low';
};

const enrichClaimWithDetail = (claim = {}, detail = null, fnolRecord = {}) => {
  const detailClaim = detail?.claim || {};
  const automation = detail?.automation || {};
  const formData = detail?.form_data || {};
  const insights = detail?.insights || {};
  const categories = getClaimCategoriesFromInsights(insights);
  const primaryCategory = getPrimaryCategory(categories);
  const priority = getAlertPriority(primaryCategory) || 'Low';
  const lineOfBusiness =
    normalizeLineOfBusiness(
      getFirstFieldValue(formData, [
        'line_of_business',
        'lineOfBusiness',
        'lob',
        'business_line',
        'policy_type',
        'policy_category',
        'coverage_type',
        'type_of_loss',
        'loss_type',
      ])
    ) ||
    normalizeLineOfBusiness(
      getFirstFieldValue(detailClaim, [
        'line_of_business',
        'lineOfBusiness',
        'lob',
        'business_line',
        'policy_type',
        'policy_category',
        'coverage_type',
        'type_of_loss',
        'loss_type',
      ])
    ) ||
    normalizeLineOfBusiness(claim.loss_type || claim.type_of_loss || fnolRecord.loss_type);

  return {
    ...claim,
    ...detailClaim,
    claim_id: claim.claim_id || detailClaim.claim_id || automation.claim_id || getClaimIdFromFnol(fnolRecord),
    fnol_id:
      claim.fnol_id ||
      detailClaim.fnol_id ||
      automation.fnol_id ||
      fnolRecord.fnol_id ||
      getFnolIdFromClaim(claim.claim_id),
    customer_name:
      claim.customer_name ||
      detailClaim.customer_name ||
      detailClaim.insured_full_name ||
      formData.insured_name ||
      formData.insured ||
      fnolRecord.customer_name ||
      '',
    form_status: claim.form_status || fnolRecord.status || 'Pending',
    claim_status: claim.claim_status || detailClaim.claim_status || 'Created',
    created_at:
      automation.processed_at ||
      detailClaim.updated_at ||
      detailClaim.created_at ||
      claim.created_at ||
      fnolRecord.processed_at ||
      '',
    exposure_amount: getExposureAmount({ claim: detailClaim, formData, insights }) || claim.exposure_amount || '',
    line_of_business: lineOfBusiness,
    loss_type: formData.loss_type || formData.type_of_loss || detailClaim.loss_type || claim.loss_type || '',
    type_of_loss: formData.type_of_loss || detailClaim.type_of_loss || claim.type_of_loss || '',
    categories,
    primaryCategory,
    priority,
  };
};

const toChartRows = (counts) =>
  Object.entries(counts).map(([name, value]) => ({ name, value }));

const buildDailyTrend = (claims, days = 15) => {
  const counts = countBy(claims, (claim) => {
    if (!claim.created_at) return 'Unknown';
    const createdAt = new Date(claim.created_at);
    if (Number.isNaN(createdAt.getTime())) return 'Unknown';
    return createdAt.toISOString().slice(0, 10);
  });

  return Array.from({ length: days }, (_, index) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (days - 1 - index));
    const dateKey = date.toISOString().slice(0, 10);
    const label = date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
    });

    return {
      date: label,
      claims: counts[dateKey] || 0,
    };
  });
};

const Dashboard = ({ onLogout, dashboardType = 'ops' }) => {
  const navigate = useNavigate();
  const isOpsDashboard = dashboardType === 'ops';
  const dashboardTitle = isOpsDashboard ? 'OPS DASHBOARD' : 'AI AGENT DASHBOARD';
  const [claims, setClaims] = useState([]);
  const [qaScores, setQaScores] = useState([]);
  const [opsFilter, setOpsFilter] = useState('total');
  const [isLoadingClaims, setIsLoadingClaims] = useState(true);
  const [claimsError, setClaimsError] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');

  useEffect(() => {
    if (!isOpsDashboard) return undefined;

    let active = true;

    const loadClaims = async () => {
      try {
        const [claimResult, fnolResult, qaResult] = await Promise.all([
          fetchFirstFnolJson('/claim-records'),
          fetchFirstFnolJson('/fnol-records').catch(() => ({ records: [] })),
          fetchFirstFnolJson('/qa-scores').catch(() => ({ scores: [] })),
        ]);
        const claimRecords = (Array.isArray(claimResult.records) ? claimResult.records : [])
          .filter((record) => !isHiddenClaimId(record.claim_id));
        const fnolRecords = (Array.isArray(fnolResult.records) ? fnolResult.records : [])
          .filter((record) => !isHiddenClaimId(getClaimIdFromFnol(record)));
        const scoreRecords = Array.isArray(qaResult.scores)
          ? qaResult.scores
          : Array.isArray(qaResult.records)
            ? qaResult.records
            : [];
        const claimById = new Map(claimRecords.map((record) => [record.claim_id, record]));
        const generatedClaimIds = new Set(
          fnolRecords.map(getClaimIdFromFnol).filter(Boolean)
        );
        const sourceClaimIds = generatedClaimIds.size
          ? Array.from(generatedClaimIds)
          : claimRecords.map((claim) => claim.claim_id).filter(Boolean);
        const claimDetails = await Promise.all(
          sourceClaimIds.map((claimId) =>
            fetchFirstFnolJson(`/claim-records/${encodeURIComponent(claimId)}`).catch(() => null)
          )
        );
        const fnolByClaimId = new Map(
          fnolRecords.map((record) => [getClaimIdFromFnol(record), record])
        );
        const generatedClaims = sourceClaimIds.map((claimId, index) =>
          enrichClaimWithDetail(
            claimById.get(claimId) || { claim_id: claimId },
            claimDetails[index],
            fnolByClaimId.get(claimId) || {}
          )
        );

        if (active) {
          setClaims(generatedClaims);
          setQaScores(scoreRecords);
          setClaimsError('');
          setUpdatedAt(new Date().toISOString());
        }
      } catch (error) {
        if (active) setClaimsError(error.message);
      } finally {
        if (active) setIsLoadingClaims(false);
      }
    };

    loadClaims();
    const refresh = window.setInterval(loadClaims, 10000);

    return () => {
      active = false;
      window.clearInterval(refresh);
    };
  }, [isOpsDashboard]);

  const opsMetrics = useMemo(() => {
    const todayKey = new Date().toISOString().slice(0, 10);
    const totalClaims = claims.length;
    const newToday = claims.filter((claim) =>
      String(claim.created_at || '').startsWith(todayKey)
    ).length;
    const filledForms = claims.filter((claim) =>
      /filled|complete/i.test(claim.form_status || '')
    ).length;
    const avgAging = totalClaims
      ? Math.round(
          claims.reduce((sum, claim) => sum + daysBetween(claim.created_at), 0) /
            totalClaims
        )
      : 0;
    const lineCounts = claims.reduce(
      (counts, claim) => {
        const lineOfBusiness = getClaimLineOfBusiness(claim);

        if (lineOfBusiness === 'Auto') counts.auto += 1;
        if (lineOfBusiness === 'Property') counts.property += 1;
        if (lineOfBusiness === 'General Liability') counts.generalLiability += 1;

        return counts;
      },
      { auto: 0, property: 0, generalLiability: 0 }
    );
    const policyRows = toChartRows(countBy(claims, (claim) => claim.policy_number));
    const dailyTrend = buildDailyTrend(claims);

    return {
      totalClaims,
      newToday,
      filledForms,
      avgAging,
      lineCounts,
      policyRows,
      dailyTrend,
    };
  }, [claims]);

  const filteredClaims = useMemo(() => {
    if (opsFilter === 'assignments') {
      return claims.filter((claim) => /filled|complete/i.test(claim.form_status || ''));
    }

    if (opsFilter === 'auto') {
      return claims.filter((claim) => getClaimLineOfBusiness(claim) === 'Auto');
    }

    if (opsFilter === 'property') {
      return claims.filter((claim) => getClaimLineOfBusiness(claim) === 'Property');
    }

    if (opsFilter === 'general-liability') {
      return claims.filter((claim) => getClaimLineOfBusiness(claim) === 'General Liability');
    }

    return claims;
  }, [claims, opsFilter]);

  const filteredTrend = useMemo(() => buildDailyTrend(filteredClaims), [filteredClaims]);

  const activeFilterLabel =
    {
      total: 'All claims',
      assignments: 'My assignments',
      auto: 'Auto',
      property: 'Property',
      'general-liability': 'General liability',
    }[opsFilter] || 'All claims';

  const priorityClaims = useMemo(() => {
    return [...filteredClaims]
      .map((claim) => {
        const aging = daysBetween(claim.created_at);
        const priority = claim.priority || getAlertPriority(claim.primaryCategory) || 'Low';
        return {
          ...claim,
          aging,
          score: getPriorityScore(claim, priority),
          priority,
        };
      })
      .sort((a, b) => b.score - a.score || b.aging - a.aging)
      .slice(0, 4);
  }, [filteredClaims]);

  const topQaScores = useMemo(() => {
    const claimById = new Map(claims.map((claim) => [claim.claim_id, claim]));

    return qaScores
      .map((score) => {
        const percent = getQualityPercent(score);
        const claim = claimById.get(score.claim_id) || {};

        return {
          ...score,
          percent,
          displayScore: formatQualityScore(score),
          tone: getQualityTone(percent),
          customer_name: claim.customer_name || score.insured_name || 'Unknown',
          fnol_id: claim.fnol_id || getFnolIdFromClaim(score.claim_id),
        };
      })
      .filter((score) => score.claim_id && score.percent !== null)
      .sort((a, b) => b.percent - a.percent || String(a.claim_id).localeCompare(String(b.claim_id)))
      .slice(0, 5)
      .map((score, index) => ({
        ...score,
        value: Math.max(score.percent, 1),
        color: qaDonutColors[index % qaDonutColors.length],
      }));
  }, [claims, qaScores]);

  return (
    <div className="dashboard-page">
      <Header onLogout={onLogout} />
      
      <div className="page-body">
        {/* SIDEBAR */}
        <aside className="sidebar mini-sidebar">
          <nav className="sidebar-nav">
            <button onClick={() => navigate('/home')} className="nav-item">
              <i className="fas fa-home"></i><span className="nav-label">Home</span>
            </button>
            <button onClick={() => navigate('/my-work')} className="nav-item">
              <i className="fas fa-briefcase"></i><span className="nav-label">My Work</span>
            </button>
            <button onClick={() => navigate('/crm')} className="nav-item">
              <i className="fas fa-address-book"></i><span className="nav-label">FNOL</span>
            </button>
            <button onClick={() => navigate('/claims')} className="nav-item">
              <i className="fas fa-file-shield"></i><span className="nav-label">Claim</span>
            </button>
            <button onClick={() => navigate('/ops-dashboard')} className={`nav-item ${isOpsDashboard ? 'active' : ''}`}>
              <i className="fas fa-tachometer-alt"></i><span className="nav-label">Ops Dashboard</span>
            </button>
            <button onClick={() => navigate('/claims?tab=quality-score')} className="nav-item quality-score-nav-item">
              <i className="fas fa-clipboard-check"></i><span className="nav-label">QA</span>
            </button>
            <button onClick={() => navigate('/agent-dashboard')} className={`nav-item ${!isOpsDashboard ? 'active' : ''}`}>
              <i className="fas fa-gauge-high"></i><span className="nav-label">AI Agent Dashboard</span>
            </button>
            <button onClick={openDocumentUploadBucket} className="nav-item">
              <i className="fas fa-file-arrow-up"></i><span className="nav-label">Document Upload</span>
            </button>
          </nav>
          <div className="sidebar-footer">
            <button onClick={onLogout} className="nav-item logout-btn">
              <i className="fas fa-sign-out-alt"></i><span className="nav-label">Logout</span>
            </button>
          </div>
        </aside>

        {isOpsDashboard ? (
          <main className="db-content ops-dashboard-content">
            <header className="db-header ops-db-header">
              <h1 className="header-title ops-title-hidden">{dashboardTitle}</h1>
              <div className="ops-status">
                <span className={claimsError ? 'error' : ''}>
                  {isLoadingClaims ? 'Loading claims...' : claimsError || formatUpdatedAt(updatedAt)}
                </span>
                <button type="button" onClick={() => window.location.reload()} aria-label="Refresh dashboard">
                  <i className="fas fa-rotate-right" aria-hidden="true"></i>
                </button>
              </div>
            </header>

            <div className="ops-cockpit-shell">
              <section className="dashboard-claim-summary" aria-label="Claim summary">
                <button
                  type="button"
                  className={`dashboard-claim-card ${opsFilter === 'total' ? 'dashboard-claim-card-active' : ''}`}
                  onClick={() => setOpsFilter('total')}
                  aria-pressed={opsFilter === 'total'}
                >
                  <span>Total</span>
                  <strong>{opsMetrics.totalClaims}</strong>
                  <svg className="ops-kpi-spark spark-gold" viewBox="0 0 80 30" aria-hidden="true">
                    <polyline points="2,24 18,17 32,20 50,9 78,13" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`dashboard-claim-card ${opsFilter === 'assignments' ? 'dashboard-claim-card-active' : ''}`}
                  onClick={() => setOpsFilter('assignments')}
                  aria-pressed={opsFilter === 'assignments'}
                >
                  <span>My assignments</span>
                  <strong>{opsMetrics.filledForms}</strong>
                  <svg className="ops-kpi-spark spark-cyan" viewBox="0 0 80 30" aria-hidden="true">
                    <polyline points="2,23 22,21 42,17 58,16 78,8" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`dashboard-claim-card ${opsFilter === 'auto' ? 'dashboard-claim-card-active' : ''}`}
                  onClick={() => setOpsFilter('auto')}
                  aria-pressed={opsFilter === 'auto'}
                >
                  <span>Auto</span>
                  <strong>{opsMetrics.lineCounts.auto}</strong>
                  <svg className="ops-kpi-spark spark-green" viewBox="0 0 80 30" aria-hidden="true">
                    <polyline points="2,26 18,17 36,10 58,8 78,12" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`dashboard-claim-card ${opsFilter === 'property' ? 'dashboard-claim-card-active' : ''}`}
                  onClick={() => setOpsFilter('property')}
                  aria-pressed={opsFilter === 'property'}
                >
                  <span>Property</span>
                  <strong>{opsMetrics.lineCounts.property}</strong>
                  <svg className="ops-kpi-spark spark-purple" viewBox="0 0 80 30" aria-hidden="true">
                    <polyline points="2,18 24,18 46,18 62,18 78,18" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`dashboard-claim-card ${opsFilter === 'general-liability' ? 'dashboard-claim-card-active' : ''}`}
                  onClick={() => setOpsFilter('general-liability')}
                  aria-pressed={opsFilter === 'general-liability'}
                >
                  <span>General liability</span>
                  <strong>{opsMetrics.lineCounts.generalLiability}</strong>
                  <svg className="ops-kpi-spark spark-red" viewBox="0 0 80 30" aria-hidden="true">
                    <polyline points="2,20 24,20 46,20 62,19 78,19" />
                  </svg>
                </button>
              </section>

              <div className="ops-overview-grid ops-cockpit-overview" aria-label="Claims overview">
                <article className="dashboard-panel ops-trend-panel">
                    <div className="dashboard-panel-title">
                      <h2>Claims Trend</h2>
                      <span>{activeFilterLabel} by created date</span>
                    </div>
                    <div className="ops-chart-frame">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={filteredTrend} margin={{ top: 8, right: 14, left: -18, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e7decf" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#667161" />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#667161" />
                          <Tooltip />
                          <Bar dataKey="claims" name="Claims" fill="#ff8a50" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                </article>

                <article className="dashboard-panel ops-qa-panel ops-qa-overview-panel">
                  <div className="dashboard-panel-title">
                    <h2>Top 5 QA Score</h2>
                    <span>Donut view</span>
                  </div>
                  <div className="ops-qa-donut-layout">
                    <div className="ops-qa-donut-chart">
                      {topQaScores.length ? (
                        <>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={topQaScores}
                                dataKey="value"
                                nameKey="claim_id"
                                innerRadius={42}
                                outerRadius={70}
                                paddingAngle={3}
                              >
                                {topQaScores.map((score) => (
                                  <Cell key={score.claim_id} fill={score.color} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value, name, item) => [item.payload.displayScore, name]} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="ops-qa-donut-center">
                            <strong>{topQaScores[0].displayScore}</strong>
                            <span>Best</span>
                          </div>
                        </>
                      ) : (
                        <div className="ops-qa-empty">No QA scores yet.</div>
                      )}
                    </div>
                    <div className="ops-qa-donut-list">
                      {topQaScores.map((score) => (
                        <button
                          type="button"
                          key={score.claim_id}
                          className="ops-qa-donut-row"
                          onClick={() =>
                            navigate(`/claims?tab=quality-score&claimId=${encodeURIComponent(score.claim_id)}`)
                          }
                        >
                          <i style={{ backgroundColor: score.color }} aria-hidden="true"></i>
                          <span>{score.claim_id}</span>
                          <strong>{score.displayScore}</strong>
                        </button>
                      ))}
                    </div>
                  </div>
                </article>

              </div>

              <div className="dashboard-workspace ops-workspace ops-cockpit-workspace">
                <section className="dashboard-main-column ops-priority-column">
                  <article className="dashboard-panel ops-priority-panel">
                    <div className="dashboard-panel-title">
                      <h2>Priority Workstream</h2>
                      <span>Oldest and active claims</span>
                    </div>
                    <div className="ops-priority-list">
                      {priorityClaims.map((claim) => (
                        <button
                          type="button"
                          className={`ops-priority-card priority-${claim.priority.toLowerCase()}`}
                          key={claim.claim_id}
                          onClick={() => navigate(`/claims?claimId=${encodeURIComponent(claim.claim_id)}`)}
                        >
                          <span className="ops-priority-score">
                            <strong>{claim.score}</strong>
                            <small>Priority</small>
                          </span>
                          <span className="ops-priority-details">
                            <span><b>Claim</b><em>{claim.claim_id}</em></span>
                            <span><b>FNOL</b><em>{claim.fnol_id || '-'}</em></span>
                            <span><b>Customer</b><em>{claim.customer_name || 'Unknown'}</em></span>
                            <span><b>Status</b><em>{claim.claim_status || '-'}</em></span>
                            <span className="ops-priority-tags">
                              <i>{claim.priority}</i>
                              <i>{claim.aging}d aging</i>
                            </span>
                          </span>
                          <span className="ops-priority-value">
                            <strong>{claim.exposure_amount || '$1,000'}</strong>
                            <small>{claim.form_status || 'No form'}</small>
                          </span>
                        </button>
                      ))}
                    </div>
                  </article>
                </section>

                <section className="dashboard-main-column">
                  <article className="dashboard-panel claims-queue-panel ops-ledger-panel">
                    <div className="dashboard-panel-title">
                      <h2>Claims Ledger</h2>
                      <span>{activeFilterLabel}</span>
                    </div>
                    <div className="dashboard-table-wrap ops-ledger-wrap">
                      <table className="dashboard-table">
                        <thead>
                          <tr>
                            <th>Claim ID</th>
                            <th>FNOL ID</th>
                            <th>Customer</th>
                            <th>Policy</th>
                            <th>Status</th>
                            <th>Form</th>
                            <th>Aging</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredClaims.map((claim) => (
                            <tr key={claim.claim_id}>
                              <td className="dashboard-id-cell">{claim.claim_id}</td>
                              <td>{claim.fnol_id}</td>
                              <td>{claim.customer_name}</td>
                              <td>{claim.policy_number}</td>
                              <td>{claim.claim_status}</td>
                              <td>{claim.form_status}</td>
                              <td>{daysBetween(claim.created_at)}d</td>
                            </tr>
                          ))}
                          {!filteredClaims.length && (
                            <tr>
                              <td colSpan="7" className="dashboard-empty-cell">
                                {claimsError || `No claims for ${activeFilterLabel}.`}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </article>
                </section>

                <aside className="dashboard-side-column ops-side-column ops-cockpit-actions" aria-label="Claim insights">
                  <article className="dashboard-panel action-panel ops-action-panel">
                    <div className="dashboard-panel-title">
                      <h2>Operational Actions</h2>
                    </div>
                    <button type="button" onClick={() => navigate('/claims')}>Open Claims</button>
                    <button type="button" onClick={() => navigate('/crm')}>Open FNOL</button>
                  </article>

                </aside>
              </div>
            </div>
          </main>
        ) : (
          <MonitoringDashboard
            ariaLabel="AI Agent Dashboard"
            className="agent-dashboard-main"
            defaultAgentId="all"
            hideAgentSelector
          />
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default Dashboard;
